import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import lodash from 'lodash';
import InputField from 'pages/study/containers/inputFields/inputFields';
import SaveFixedFooter from 'pages/studyOverview/components/SaveFixedFooter';
import { PageTitle, PageDescription } from 'components/common/Study/PageComponents';
import styles from './StudySettingsDetails.scss';
import { 
    combineMetadata,
    normalizeValues,
    formatMetadataToAdd,
    formatMetadataToRemove,
    getMetadataToUpdate,
    formatValue,
    formatViewValue,
    assignDataSourceToMetadata,
} from './utils/helperFunctions';

import {
    getStudyDescriptors,
    updateStudyDetails,
    getBinderDescriptors,
} from 'pages/studyOverview/actions/studySettings';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import { manageStudyMetadataPredicate } from 'common/permissions/studyPredicates';
import { IncompleteModal } from 'pages/study/containers/modals/IncompleteModal';

export class StudySettingsDetails extends Component {
    constructor(props) {
        super();
        this.state = {
            changed: false,
            binderMetadataValues : props.initialBinderMetadataValues,
            incompleteModalOpen: false,
        };

        this.isValid = this.isValid.bind(this);
        this.closeIncompleteModal = this.closeIncompleteModal.bind(this);
        this.acceptIncompleteModal = this.acceptIncompleteModal.bind(this);
        this.updateMetadata = this.updateMetadata.bind(this);
    }

    isValid(binderMetadata, allBinderDescriptors) {
        return Object.keys(binderMetadata).length === allBinderDescriptors.length && lodash.every(binderMetadata, metadata => {
            return !lodash.isNil(metadata) && !lodash.isEmpty(metadata);
        });
    }

    closeIncompleteModal() {
        this.setState({incompleteModalOpen: false});
    }

    acceptIncompleteModal() {
        this.setState({incompleteModalOpen: false});
        this.updateMetadata();
    }

    componentDidMount() {
        const { 
            study : { institutionId, studyTypeId }, 
            getBinderDescriptors, 
            getStudyDescriptors 
        } = this.props;
        getBinderDescriptors(institutionId, studyTypeId);
        getStudyDescriptors(institutionId, studyTypeId);
    }

    onValueChange(descriptorId, value) {
        const { allBinderDescriptorsByDescriptorId } = this.props;

        const correspondingBinderMetadata = allBinderDescriptorsByDescriptorId[descriptorId];
        const newBinderMetadataValues = {
            ...this.state.binderMetadataValues,
            [descriptorId]: normalizeValues(value, correspondingBinderMetadata.inputFieldType)
        };

        this.setState({
            changed: true,
            binderMetadataValues: newBinderMetadataValues
        });
    }

    onSubmit() {
        const { binderMetadataValues } = this.state;
        const { allBinderDescriptors } = this.props;
        // if the length of binderMetadataValues is less than the length of allBinderDescriptors, or if any of the
        // values are null, or have a length of zero, then we need to show the incomplete modal
        if (!this.isValid(binderMetadataValues, allBinderDescriptors)) {
            this.setState({incompleteModalOpen: true});
            return;
        }
        this.updateMetadata();
    }

    updateMetadata() {
        const { binderMetadataValues } = this.state;
        const { initialBinderMetadataValues, binderMetadata, allBinderDescriptors } = this.props;
        
        const { 
            addCollection, 
            removeCollection 
        } = getMetadataToUpdate(initialBinderMetadataValues, binderMetadataValues);
        const binderMetadataToAdd = formatMetadataToAdd(addCollection, allBinderDescriptors);
        const binderMetadataToRemove = formatMetadataToRemove(removeCollection, binderMetadata); 

        this.props.updateStudyDetails(binderMetadataToAdd, binderMetadataToRemove);
    }

    renderStudyDetailsView() {
        const { allBinderDescriptors } = this.props;
        const result = allBinderDescriptors.map((metadata) => {
            return (
                <div key={metadata.descriptorId} data-cy="StudySettingsDetails">
                    <header
                        className={styles.label}
                        data-cy="StudySettingsDetails-name"
                    >
                        {metadata.name}
                    </header>
                    <p className={styles.value} data-cy="StudySettingsDetails-value">
                        {lodash.defaultTo(formatViewValue(metadata.value), '-')}
                    </p>
                </div>
            );
        });
        return(
            <div  data-cy="StudySettingsDetails-result">
                {result}
            </div>
        );
    }

    renderStudyDetailsManage() {
        const { allBinderDescriptors } = this.props;
        const { binderMetadataValues } = this.state;
        
        return (<Fragment data-cy="StudySettingsDetails-Fragment">
            <PageDescription>
            Editing these fields will change which documents are automatically filed into the study.
            </PageDescription>
            <BinderMetadataFields 
                allBinderDescriptors={allBinderDescriptors} 
                binderMetadataValues={binderMetadataValues} 
                onValueChange={(descriptorId, value) =>this.onValueChange(descriptorId, value)}  
            />
            <SaveFixedFooter
                disabled={!this.state.changed}
                onClick={() =>this.onSubmit()}
                data-cy="StudySettingsDetails-SaveFixedFooter"
            >
            Save Changes
            </SaveFixedFooter>
            <IncompleteModal 
                isOpen={this.state.incompleteModalOpen} 
                onClose={this.closeIncompleteModal} 
                onSave={this.acceptIncompleteModal}
            />
        </Fragment>);
    }
    
    render(){
        const { isLoading, canManageStudyMetadata } = this.props;        
        if(isLoading) {
            return <LoadingStatus itemName={'study settings'} loading={true} filterQuery={false} />;
        }

        return(
            <div className={styles.studyDetails} data-cy="StudySettingsDetails-return">
                <PageTitle>
                    Study Setup
                </PageTitle>
                {canManageStudyMetadata ? this.renderStudyDetailsManage() : this.renderStudyDetailsView()}
            </div>
        );
    }
}

export function BinderMetadataFields(props){

    const { allBinderDescriptors, binderMetadataValues, onValueChange } = props;

    /**
     * The binder descriptors here are being processed in the following:
     *  Source: src/js/apps/pages/study/reducers/createStudy.js
     *  Function: translateBinderTemplateRequirementToStudyRequirment
     */
    const result =  allBinderDescriptors.map(metadata => {
        const {
            id,
            name,
            descriptorId,
            dataSources,
            inputFieldType,
            dataType,
            instructions
        } = metadata;
        const value = formatValue(binderMetadataValues, metadata);

        return (
            <InputField
                id={id}
                name={name}
                value={value}
                dataType={dataType}
                key={descriptorId}
                dataSources={dataSources}
                targetId={descriptorId}
                inputFieldType={inputFieldType}
                onValueChange={onValueChange}
                instructions={instructions}
                placeholder={'Not Selected'}
                data-cy="StudySettingsDetails-InputField"
            />
        );
    });

    return <div data-cy="StudySettingsDetails-results">{result}</div>;
}

export default connect((state, props) => {
    const studyId = lodash.get(state, 'study.id', null);
    const binderMetadata = lodash.get(props, 'binderMetadata', {});
    const initialBinderMetadataValues = !lodash.isEmpty(binderMetadata) ? combineMetadata(binderMetadata) : {};
    const dataSourceItems = lodash.get(state, 'dataSourceItems', {});
    const binderDescriptors = lodash.get(state, 'studySettings.binderDescriptors', []);
    const staffDescriptor = lodash.get(state, 'studySettings.staffDescriptor', {});
    const binderDescriptorsWithoutStaffDescriptor = binderDescriptors.filter(bd => bd.descriptorId !== staffDescriptor.descriptorId);
    const allBinderDescriptors = assignDataSourceToMetadata(binderDescriptorsWithoutStaffDescriptor, dataSourceItems, studyId, initialBinderMetadataValues);
    const allBinderDescriptorsByDescriptorId = lodash.keyBy(allBinderDescriptors, 'descriptorId');
    const canManageStudyMetadata = manageStudyMetadataPredicate(state);
    const isLoading = lodash.isEmpty(allBinderDescriptorsByDescriptorId)
        || lodash.isEmpty(staffDescriptor);
    return {
        isLoading,
        binderMetadata,
        initialBinderMetadataValues,
        allBinderDescriptors,
        allBinderDescriptorsByDescriptorId,
        canManageStudyMetadata,
    };
}, {
    getStudyDescriptors,
    updateStudyDetails,
    getBinderDescriptors,
})(StudySettingsDetails);
